import * as React from "react";
import { Formik, FormikProps } from "formik";
import { useHistory, useLocation } from "react-router";
import {
  OrganizationSurveysServiceProps,
  OrganizationSurveysService,
} from "../../../../application/service/service-provider/surveys-service";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { useEffect, useState } from "react";
import { useModal } from "../../../../utils/custom-hooks";
import {
  SurveyStatusTag,
  connectPageService,
  BaseModal,
} from "@graffer-inc/smart-apply";
import { formatDatetime } from "../../../../utils/date-utils";
import { SecondaryButton } from "@graffer-inc/console-design-kit";
import { GroupShareSelectField } from "../../../molecules/group-share-select-field";
import classnames from "classnames";
import { useParams } from "react-router-dom";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../service-provider/routes";
import { SurveysServiceProviderR } from "./route";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { Pagination } from "@graffer-inc/console-design-kit";
import { emptyFunction } from "../../../../utils/function-utils";

const SurveyImportFromFileModal: React.FC<{
  tenantId: string;
  isActive: boolean;
  isLoading: boolean;
  close: () => void;
  onSave: (jsonData: File, groupId: string) => void;
}> = ({ tenantId, isActive, isLoading, close, onSave }) => {
  const [jsonData, setJsonData] = useState<File>();
  const [groupId, setGroupId] = useState<string>();

  return (
    <BaseModal
      isActive={isActive}
      title="アンケートインポート"
      onClose={close}
      hasOverflow={true}
    >
      <Formik
        validateOnChange={true}
        initialValues={{}}
        onSubmit={emptyFunction}
      >
        {({ handleSubmit }: FormikProps<object>) => (
          <form onSubmit={handleSubmit}>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">アンケート情報ファイル</label>
                <div className="file has-name">
                  <label className="file-label">
                    <input
                      className="file-input"
                      type="file"
                      name="resume"
                      accept={"application/json"}
                      onChange={({ target: { files } }) => {
                        if (!files || !(files[0] instanceof Blob)) {
                          return;
                        }
                        const file = files[0];
                        const fileReader = new FileReader();
                        fileReader.readAsText(file);
                        fileReader.onload = function () {
                          setJsonData(file);
                        };
                      }}
                    />
                    <span className="file-cta">
                      <span className="file-icon">
                        <i className="fas fa-upload" />
                      </span>
                      <span className="file-label">
                        {jsonData?.name ?? "ファイルを選択…"}
                      </span>
                    </span>
                    <span className="file-name">
                      {jsonData ? "選択済み" : "未選択"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="field">
                <GroupShareSelectField
                  tenantId={tenantId}
                  selectedGroupId={groupId || ""}
                  onChange={setGroupId}
                />
              </div>
            </section>
            <section className="modal-card-foot">
              <button
                type="button"
                className="button is-failure is-fullwidth"
                onClick={close}
              >
                キャンセルする
              </button>
              <button
                type="submit"
                className={classnames("button is-info is-fullwidth", {
                  "is-loading": isLoading,
                })}
                onClick={() => {
                  if (!jsonData || !groupId) {
                    return;
                  }
                  onSave(jsonData, groupId);
                }}
                disabled={!jsonData || !groupId || isLoading}
              >
                インポートする
              </button>
            </section>
          </form>
        )}
      </Formik>
    </BaseModal>
  );
};

const OrganizationSurveysList: React.FC<OrganizationSurveysServiceProps> = ({
  surveys,
  paging,
  setPage,
  movePage,
  queryName,
  setName,
  fetchSurveysByTenantId,
  searchSurveys,
  importSurveyFromFile,
}) => {
  const { page, name } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
  };
  const { id: tenantId } = useParams<{ id: string }>();
  const history = useHistory();
  const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
  const importSurveyModal = useModal();

  useEffect(() => {
    setPage(parseInt(page!, 10) || 1);
    setName(name || "");
    fetchSurveysByTenantId(tenantId);
    // eslint-disable-next-line
  }, [page, name, setPage, setName, fetchSurveysByTenantId]);

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns">
          <div className="column">
            <ServiceProviderTabs orgId={tenantId} />
            <div className="columns">
              <div className="column">
                <div className="field has-addons">
                  <p className="control has-icons-left has-icons-right">
                    <input
                      className="input"
                      placeholder="アンケート名称で検索"
                      value={queryName}
                      onChange={(e) => setName(e.target.value)}
                      style={{
                        fontSize: "14px",
                      }}
                    />
                    <span className="icon is-small is-left">
                      <i className="fas fa-search" />
                    </span>
                    <span
                      className="icon is-small is-right"
                      role="button"
                      onClick={() => {
                        setName("");
                        searchSurveys();
                      }}
                      style={{ pointerEvents: "initial", cursor: "pointer" }}
                    >
                      <i className="fas fa-times" />
                    </span>
                  </p>
                  <p className="control">
                    <SecondaryButton text="検索" onClick={searchSurveys} />
                  </p>
                </div>
              </div>
              <div className="column has-text-right is-right is-narrow">
                <div className="has-text-right buttons is-right">
                  <SecondaryButton
                    text="アンケートをインポートする"
                    onClick={importSurveyModal.open}
                  />
                </div>
              </div>
            </div>
            <div className="table-container">
              <table className="table is-fullwidth">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>アンケート名称</th>
                    <th>公開ステータス</th>
                    <th>受付開始日時</th>
                    <th>受付終了日時</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys?.map((survey) => (
                    <tr
                      key={survey.id}
                      className="c-tr-selectable"
                      onClick={() =>
                        history.push(
                          SurveysServiceProviderR.SurveyDetail.link({
                            tenantId: tenantId,
                            surveyId: survey.id,
                          }),
                        )
                      }
                    >
                      <td>{survey.id}</td>
                      <td>{survey.name}</td>
                      <td>
                        <SurveyStatusTag surveyStatus={survey.publishStatus} />
                      </td>
                      <td>
                        {survey.publishSetting.startAcceptAt
                          ? formatDatetime(survey.publishSetting.startAcceptAt)
                          : "設定なし"}
                      </td>
                      <td>
                        {survey.publishSetting.endAcceptAt
                          ? formatDatetime(survey.publishSetting.endAcceptAt)
                          : "設定なし"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: "16px" }}>
                <Pagination
                  onPageClick={movePage}
                  currentPage={paging.current}
                  lastPage={paging.total}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <SurveyImportFromFileModal
        tenantId={tenantId}
        isActive={importSurveyModal.isActive}
        isLoading={isImportLoading}
        close={importSurveyModal.close}
        onSave={async (jsonData, groupId) => {
          setIsImportLoading(true);
          const data = await importSurveyFromFile(jsonData, tenantId, groupId);
          setIsImportLoading(false);
          importSurveyModal.close();
          if (!data) {
            return;
          }
          history.push(
            SurveysServiceProviderR.SurveyDetail.link({
              tenantId: tenantId,
              surveyId: data.id,
            }),
          );
        }}
      />
    </ServiceProviderTemplate>
  );
};

export const OrganizationSurveysListContainer = connectPageService(
  OrganizationSurveysList,
  () => new OrganizationSurveysService(),
);
